import React, {useState, useEffect} from "react";
import {
    Monitor,
    Shield,
    Lock,
    Eye,
    EyeOff,
    Loader,
    CheckCircle2,
    XCircle,
    RefreshCw,
    Unlock,
    ChevronDown,
    ChevronUp,
    Info,
    User
} from "lucide-react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {useQueryClient, useQuery} from '@tanstack/react-query';
import {Modal, ListLoader, inputClass} from "./SettingsUI";
import AppleSelect from "../Common/AppleSelect";

const SpinningLoader = (props) => <Loader {...props} className={`${props.className} animate-spin`}/>;

// ─── Constantes ──────────────────────────────────────────────────────────────

const MODULE_GROUPS = [
    {
        group: 'Général',
        modules: [
            {key: 'dashboard',       label: 'Tableau de bord'},
            {key: 'notifications',   label: 'Notifications'},
        ]
    },
    {
        group: 'Ventes & Clients',
        modules: [
            {key: 'sales',           label: 'Ventes'},
            {key: 'clients',         label: 'Clients'},
            {key: 'waiting_list',    label: "Liste d'attente"},
            {key: 'loyalty',         label: 'Fidélité'},
            {key: 'returns',         label: 'Retours & avoirs'},
            {key: 'discounts',       label: 'Remises'},
            {key: 'payments',        label: 'Paiements'},
        ]
    },
    {
        group: 'Catalogue & Stock',
        modules: [
            {key: 'catalogue',       label: 'Catalogue'},
            {key: 'stock',           label: 'Stock'},
            {key: 'recipes',         label: 'Recettes / Fiches'},
            {key: 'purchase_orders', label: 'Bons de commande'},
            {key: 'providers',       label: 'Fournisseurs'},
            {key: 'ecommerce',       label: 'E-commerce'},
        ]
    },
    {
        group: 'Planning & RH',
        modules: [
            {key: 'calendar',        label: 'Calendrier'},
            {key: 'planning',        label: 'Planning'},
            {key: 'attendance',      label: 'Pointage'},
            {key: 'employees',       label: 'Employés'},
            {key: 'commissions',     label: 'Commissions'},
        ]
    },
    {
        group: 'Finance & Admin',
        modules: [
            {key: 'accounting',      label: 'Comptabilité'},
            {key: 'reports',         label: 'Rapports'},
            {key: 'audit',           label: 'Audit'},
            {key: 'settings',        label: 'Paramètres'},
        ]
    },
];

const MODULES = MODULE_GROUPS.flatMap(g => g.modules);

const ACTIONS = ['read', 'create', 'update', 'delete'];

const EMPTY_PERMISSIONS = Object.fromEntries(MODULES.map(m => [m.key, []]));

// Doit correspondre à la règle de l'API (EmployeeBackofficeController)
const MIN_PASSWORD_LENGTH = 8;
const PASSWORD_LENGTH_ERROR = `Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères`;

// ─── Sous-composants ──────────────────────────────────────────────────────────

const PermissionsMatrix = ({permissions, onChange, disabled}) => {
    // Toggle all actions for a module row
    const toggleRow = (key) => {
        if (disabled) return;
        const current = permissions[key] || [];
        const next = current.length === ACTIONS.length ? [] : [...ACTIONS];
        onChange({...permissions, [key]: next});
    };

    // Toggle one action for all modules in a group
    const toggleGroupAction = (modules, action) => {
        if (disabled) return;
        const allChecked = modules.every(({key}) => (permissions[key] || []).includes(action));
        const updated = {...permissions};
        modules.forEach(({key}) => {
            const current = updated[key] || [];
            updated[key] = allChecked
                ? current.filter(a => a !== action)
                : current.includes(action) ? current : [...current, action];
        });
        onChange(updated);
    };

    return (
        <div className="overflow-x-auto -mx-1">
            <table className="w-full text-xs min-w-[340px]">
                <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 pl-2 pr-3 font-medium text-gray-500 w-36">Module</th>
                    {ACTIONS.map(a => (
                        <th key={a} className="text-center py-2 px-1 font-medium text-gray-400 capitalize w-14">
                            {a === 'read' ? 'Lire' : a === 'create' ? 'Créer' : a === 'update' ? 'Modifier' : 'Suppr.'}
                        </th>
                    ))}
                    <th className="w-8"/>
                </tr>
                </thead>
                <tbody>
                {MODULE_GROUPS.map(({group, modules}) => (
                    <React.Fragment key={group}>
                        {/* Group header row */}
                        <tr className="bg-gray-50 dark:bg-gray-700/50 border-t border-b border-gray-200 dark:border-gray-700">
                            <td colSpan={2} className="py-1.5 pl-2 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                                {group}
                            </td>
                            {ACTIONS.slice(1).map(action => {
                                const allChecked = modules.every(({key}) => (permissions[key] || []).includes(action));
                                return (
                                    <td key={action} className="text-center py-1.5 px-1">
                                        <button
                                            type="button"
                                            title={allChecked ? 'Tout décocher' : 'Tout cocher'}
                                            onClick={() => toggleGroupAction(modules, action)}
                                            disabled={disabled}
                                            className={'w-4 h-4 mx-auto rounded border flex items-center justify-center transition-colors '
                                                + (allChecked
                                                    ? 'bg-primary border-primary text-white'
                                                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-primary')}
                                        >
                                            {allChecked && <span className="text-[8px] font-bold">✓</span>}
                                        </button>
                                    </td>
                                );
                            })}
                            <td/>
                        </tr>
                        {/* Module rows */}
                        {modules.map(({key, label}) => {
                            const rowActions = permissions[key] || [];
                            const allChecked = rowActions.length === ACTIONS.length;
                            return (
                                <tr key={key} className="hover:bg-primary/5 dark:hover:bg-primary/10 border-b border-gray-100 dark:border-gray-700 transition-colors">
                                    <td className="py-2 pl-4 pr-2 text-gray-700 dark:text-gray-300 font-medium">{label}</td>
                                    {ACTIONS.map(action => {
                                        const checked = rowActions.includes(action);
                                        return (
                                            <td key={action} className="text-center py-2 px-1">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    disabled={disabled}
                                                    onChange={() => {
                                                        if (disabled) return;
                                                        const next = checked
                                                            ? rowActions.filter(a => a !== action)
                                                            : [...rowActions, action];
                                                        onChange({...permissions, [key]: next});
                                                    }}
                                                    className="w-3.5 h-3.5 rounded text-primary border-gray-300 dark:border-gray-600 focus:ring-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                                                />
                                            </td>
                                        );
                                    })}
                                    <td className="text-center py-2 pr-1">
                                        <button
                                            type="button"
                                            onClick={() => toggleRow(key)}
                                            disabled={disabled}
                                            title={allChecked ? 'Tout décocher' : 'Tout cocher'}
                                            className="text-[10px] text-gray-400 hover:text-primary transition-colors disabled:opacity-30"
                                        >
                                            {allChecked ? '✕' : '✓'}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </React.Fragment>
                ))}
                </tbody>
            </table>
        </div>
    );
};

const StatusBadge = ({enabled}) => (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
        enabled
            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
    }`}>
        {enabled
            ? <><CheckCircle2 className="w-3.5 h-3.5"/>Activé</>
            : <><XCircle className="w-3.5 h-3.5"/>Désactivé</>
        }
    </span>
);

// ─── Composant principal ──────────────────────────────────────────────────────

const BackofficeAccessModal = ({employee, onClose, showNotification}) => {
    const queryClient = useQueryClient();

    // État courant de l'accès backoffice
    const [backofficeStatus, setBackofficeStatus] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(true);

    // Formulaire activation / mise à jour
    const [role, setRole] = useState('');
    const [backofficePassword, setBackofficePassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [permissions, setPermissions] = useState(EMPTY_PERMISSIONS);
    const [showPermissions, setShowPermissions] = useState(false);

    // États opérations
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    // ── Fetch des rôles dynamiques depuis /profils ─────────────────────────────
    const {data: profiles = [], isLoading: loadingProfiles} = useQuery({
        queryKey: ['profiles'],
        queryFn: async () => {
            const accountId = await secureStorage.getItem("selectedAccount");
            const response = await api.get('/profils', {
                headers: {'X-account-id': accountId.id, 'Accept': 'application/json'}
            });
            return response.data || [];
        },
        staleTime: 5 * 60 * 1000
    });

    // ── Chargement du statut backoffice ────────────────────────────────────────
    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const accountId = await secureStorage.getItem("selectedAccount");
                const response = await api.get(`/employees/backoffice/${employee.id}`, {
                    headers: {'X-account-id': accountId.id, 'Accept': 'application/json'}
                });
                const data = response.data;
                setBackofficeStatus(data);

                // Pré-remplir le formulaire si déjà activé
                if (data.backoffice_enabled) {
                    setRole(data.backoffice_role || '');
                    setPermissions(data.backoffice_permissions || EMPTY_PERMISSIONS);
                }
            } catch (err) {
                if (err.response?.status !== 404) {
                    console.error("Erreur chargement statut backoffice:", err);
                }
                setBackofficeStatus({backoffice_enabled: false});
            } finally {
                setLoadingStatus(false);
            }
        };
        fetchStatus();
    }, [employee.id]);

    // ── Quand on sélectionne un rôle dans le select ────────────────────────────
    const handleRoleChange = (newRole) => {
        setRole(newRole);
        // Réinitialise les permissions à vide (l'admin configurera si besoin)
        setPermissions(EMPTY_PERMISSIONS);
        if (errors.role) setErrors(prev => ({...prev, role: null}));
    };

    // ── Validation ─────────────────────────────────────────────────────────────
    const validate = (isEnable) => {
        const errs = {};
        if (!role) {
            errs.role = "Le rôle est requis";
        }
        if (isEnable && !backofficePassword.trim()) {
            errs.backofficePassword = "Le mot de passe backoffice est requis";
        }
        if (isEnable && backofficePassword.trim() && backofficePassword.trim().length < MIN_PASSWORD_LENGTH) {
            errs.backofficePassword = PASSWORD_LENGTH_ERROR;
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Activer l'accès backoffice ──────────────────────────────────────────────
    const handleEnable = async () => {
        if (!validate(true)) return;
        setSubmitting(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const payload = {
                backoffice_role: role,
                backoffice_password: backofficePassword,
                backoffice_permissions: permissions,
            };

            await api.put(`/employees/backoffice/${employee.id}/enable`, payload, {
                headers: {'X-account-id': accountId.id, 'Content-Type': 'application/json'}
            });

            showNotification('success', `Accès backoffice activé pour ${employee.name}`);
            setBackofficeStatus({...backofficeStatus, backoffice_enabled: true, backoffice_role: role});
            queryClient.invalidateQueries({queryKey: ['employees']});
            setBackofficePassword('');
        } catch (err) {
            const msg = err.response?.data?.detail || err.response?.data?.message || "Erreur lors de l'activation";
            showNotification('error', msg);
        } finally {
            setSubmitting(false);
        }
    };

    // ── Désactiver l'accès backoffice ──────────────────────────────────────────
    const handleDisable = async () => {
        setSubmitting(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            await api.put(`/employees/backoffice/${employee.id}/disable`, {}, {
                headers: {'X-account-id': accountId.id, 'Accept': 'application/json'}
            });

            showNotification('success', `Accès backoffice désactivé pour ${employee.name}`);
            setBackofficeStatus({...backofficeStatus, backoffice_enabled: false});
            queryClient.invalidateQueries({queryKey: ['employees']});
        } catch (err) {
            const msg = err.response?.data?.detail || err.response?.data?.message || "Erreur lors de la désactivation";
            showNotification('error', msg);
        } finally {
            setSubmitting(false);
        }
    };

    // ── Mettre à jour role/permissions/mot de passe ────────────────────────────
    const handleUpdate = async () => {
        if (!validate(false)) return;
        setSubmitting(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const payload = {
                backoffice_role: role,
                backoffice_permissions: permissions,
            };
            if (backofficePassword.trim()) {
                if (backofficePassword.trim().length < MIN_PASSWORD_LENGTH) {
                    setErrors({backofficePassword: PASSWORD_LENGTH_ERROR});
                    setSubmitting(false);
                    return;
                }
                payload.backoffice_password = backofficePassword;
            }

            await api.put(`/employees/backoffice/${employee.id}/update`, payload, {
                headers: {'X-account-id': accountId.id, 'Content-Type': 'application/json'}
            });

            showNotification('success', 'Accès backoffice mis à jour');
            setBackofficeStatus({...backofficeStatus, backoffice_role: role});
            queryClient.invalidateQueries({queryKey: ['employees']});
            setBackofficePassword('');
        } catch (err) {
            const msg = err.response?.data?.detail || err.response?.data?.message || "Erreur lors de la mise à jour";
            showNotification('error', msg);
        } finally {
            setSubmitting(false);
        }
    };

    // ── Reset mot de passe ────────────────────────────────────────────────────
    const handleRequestReset = async () => {
        setSubmitting(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            await api.post(`/employees/backoffice/${employee.id}/request-reset`, {}, {
                headers: {'X-account-id': accountId.id, 'Accept': 'application/json'}
            });
            showNotification('success', 'Email de réinitialisation envoyé');
        } catch (err) {
            const msg = err.response?.data?.detail || err.response?.data?.message || "Erreur lors du reset";
            showNotification('error', msg);
        } finally {
            setSubmitting(false);
        }
    };

    // ── Déverrouiller compte ──────────────────────────────────────────────────
    const handleUnlock = async () => {
        setSubmitting(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            await api.post(`/employees/backoffice/${employee.id}/unlock`, {}, {
                headers: {'X-account-id': accountId.id, 'Accept': 'application/json'}
            });
            showNotification('success', 'Compte déverrouillé');
            setBackofficeStatus({...backofficeStatus, is_locked: false});
        } catch (err) {
            const msg = err.response?.data?.detail || err.response?.data?.message || "Erreur lors du déverrouillage";
            showNotification('error', msg);
        } finally {
            setSubmitting(false);
        }
    };

    const isEnabled = backofficeStatus?.backoffice_enabled;
    const isLocked  = backofficeStatus?.is_locked;

    // ── Rendu ──────────────────────────────────────────────────────────────────
    const footer = !loadingStatus && (
        <div className="grid grid-cols-2 border-t border-gray-200 dark:border-gray-700">
            <button type="button" onClick={onClose} disabled={submitting}
                    className="py-4 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium disabled:opacity-50">
                Annuler
            </button>
            <button type="button" onClick={isEnabled ? handleUpdate : handleEnable} disabled={submitting}
                    className="py-4 font-medium border-l border-gray-200 dark:border-gray-700 text-primary hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center justify-center gap-2">
                {submitting ? <Loader className="w-4 h-4 animate-spin"/> : isEnabled ? <CheckCircle2 className="w-4 h-4"/> : <Monitor className="w-4 h-4"/>}
                {isEnabled ? 'Mettre à jour' : "Activer l'accès"}
            </button>
        </div>
    );

    const secondaryButtonClass = 'flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium rounded transition-colors disabled:opacity-50';

    return (
        <Modal title="Accès Backoffice" subtitle={employee.name} onClose={onClose} footer={footer} size="lg">
            {loadingStatus ? (
                <ListLoader label="Chargement du statut…"/>
            ) : (
                <div className="space-y-4">
                    <div className="flex justify-center"><StatusBadge enabled={isEnabled}/></div>

                    {/* Alerte compte verrouillé */}
                    {isLocked && (
                        <div className="flex items-center justify-between gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
                            <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
                                <Lock className="w-4 h-4 flex-shrink-0"/>
                                <span className="text-sm font-medium">Compte verrouillé (tentatives excessives)</span>
                            </div>
                            <button
                                type="button"
                                onClick={handleUnlock}
                                disabled={submitting}
                                className={`${secondaryButtonClass} text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40`}
                            >
                                <Unlock className="w-4 h-4"/>
                                Déverrouiller
                            </button>
                        </div>
                    )}

                    {/* Info si déjà activé */}
                    {isEnabled && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 flex items-start gap-2">
                            <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0"/>
                            <p className="text-xs text-blue-800 dark:text-blue-300">
                                Cet employé peut se connecter au backoffice avec son email et son mot de passe backoffice.
                                Modifiez le rôle ou les permissions puis cliquez sur <strong>Mettre à jour</strong>.
                            </p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* ── Sélection du rôle — dynamique via /profils ── */}
                        <div>
                            <label htmlFor="bo-role" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                Rôle backoffice<span className="text-red-500"> *</span> :
                            </label>
                            <AppleSelect
                                id="bo-role"
                                value={role}
                                onChange={e => handleRoleChange(e.target.value)}
                                disabled={loadingProfiles}
                                error={errors.role}
                                icon={loadingProfiles ? SpinningLoader : User}
                                placeholder={loadingProfiles ? "Chargement…" : "— Sélectionner un rôle —"}
                                options={profiles.map(p => ({
                                    value: p.name || p.label,
                                    label: (p.name || p.label || '').replace('ROLE_', ''),
                                }))}
                            />
                            {errors.role && <p className="mt-1 text-xs sm:text-sm text-red-500">{errors.role}</p>}
                        </div>

                        {/* Mot de passe backoffice */}
                        <div>
                            <label htmlFor="bo-password" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                {isEnabled ? 'Nouveau mot de passe' : 'Mot de passe backoffice'} :
                            </label>
                            <div className="relative">
                                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
                                <input
                                    id="bo-password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={backofficePassword}
                                    onChange={e => {
                                        setBackofficePassword(e.target.value);
                                        if (errors.backofficePassword) setErrors(prev => ({...prev, backofficePassword: null}));
                                    }}
                                    placeholder={isEnabled ? '••••••••' : `Minimum ${MIN_PASSWORD_LENGTH} caractères`}
                                    autoComplete="new-password"
                                    className={`${inputClass(errors.backofficePassword)} pl-9 pr-10`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(v => !v)}
                                    tabIndex={-1}
                                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                                </button>
                            </div>
                            {errors.backofficePassword
                                ? <p className="mt-1 text-xs sm:text-sm text-red-500">{errors.backofficePassword}</p>
                                : isEnabled && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Laisser vide pour ne pas le modifier.</p>}
                        </div>
                    </div>

                    {/* Permissions détaillées */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        <button
                            type="button"
                            onClick={() => setShowPermissions(v => !v)}
                            aria-expanded={showPermissions}
                            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            <span className="flex items-center gap-2">
                                <Shield className="w-4 h-4 text-gray-400"/>
                                Permissions détaillées
                            </span>
                            {showPermissions
                                ? <ChevronUp className="w-4 h-4 text-gray-400"/>
                                : <ChevronDown className="w-4 h-4 text-gray-400"/>
                            }
                        </button>
                        {showPermissions && (
                            <div className="px-4 py-3">
                                <PermissionsMatrix
                                    permissions={permissions}
                                    onChange={setPermissions}
                                    disabled={false}
                                />
                            </div>
                        )}
                    </div>

                    {/* Actions secondaires (si déjà activé) */}
                    {isEnabled && (
                        <div className="flex flex-wrap justify-between gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <button
                                type="button"
                                onClick={handleRequestReset}
                                disabled={submitting}
                                className={`${secondaryButtonClass} text-primary hover:bg-primary/10`}
                            >
                                <RefreshCw className="w-4 h-4"/>
                                Envoyer reset password
                            </button>
                            <button
                                type="button"
                                onClick={handleDisable}
                                disabled={submitting}
                                className={`${secondaryButtonClass} text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20`}
                            >
                                {submitting ? <Loader className="w-4 h-4 animate-spin"/> : <XCircle className="w-4 h-4"/>}
                                Désactiver l'accès
                            </button>
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
};

export default BackofficeAccessModal;
